/**
 * Deterministically expand a compact `ThemeSpec` (mode + 5 brand colors + background) into a full
 * `Theme`. This is the counterpart to the backend/CLI that only produces the small spec — all the
 * visual derivation (surfaces, buttons, hover states, a 12-color chart palette, AG Grid params)
 * lives here, tuned once, using the OKLCH + WCAG utilities on `Color`.
 *
 * Kept intentionally free of hand-tuning per brand: given the same spec it always produces the same
 * Theme, so custom themes scale without a 130-line hand-written object each.
 */

import { createLineFillGradientPlugin } from "./chartjs-plugins";
import { Color, ensureContrast, readableText } from "./color";
import {
  GradientLayer,
  LinearLayer,
  ModeBackground,
  PALETTE_BACKGROUNDS,
  PaletteBackground,
  RadialLayer,
} from "./paletteBackgrounds";
import { PALETTE_CHART_NEGATIVE, PALETTE_NEUTRAL } from "./palettes";
import { Theme, ThemeSpec } from "./types";

const NUM_CHART_COLORS = 12;

const WHITE = Color.parse("#ffffff");
const NEAR_BLACK = Color.parse("#111827");
/**
 * Dark-mode card surface. A DESIGNED value, and deliberately NEUTRAL (zero chroma) — unlike the
 * main/nav surfaces below it is intentionally not tinted toward the palette's primary, so a card
 * reads identically across all eight palettes. Do not "restore" the oklch(L, surfaceChroma, hue)
 * pattern its siblings use.
 */
const DARK_CARD_SURFACE = Color.parse("#25262A");

/** Build a Color from OKLCH components (perceptually-uniform tints/shades of a brand hue). */
function oklch(l: number, c: number, h: number): Color {
  // Round to a safe precision before serializing. A near-neutral brand color has a chroma near 0
  // (e.g. 3.6e-9), which template-interpolates in scientific notation and is rejected by
  // Color.parse's oklch regex — so round it down to plain "0". Guard NaN hue (pure grays) too.
  const round = (n: number, dp: number) => (Number.isFinite(n) ? Number(n.toFixed(dp)) : 0);
  return Color.parse(`oklch(${round(l, 4)} ${round(Math.max(0, c), 4)} ${round(h, 3)})`);
}

/** Expand the 5 brand colors into a 12-color chart palette by adding on-brand shades. */
function buildChartPalette(brand: Color[], isDark: boolean): string[] {
  const palette: Color[] = [...brand];
  // Alternate lighter/darker/more-saturated variants of the brand colors, cycling through them,
  // until we have 12. Slightly brighten everything in dark mode for legibility on dark surfaces.
  const transforms: ((c: Color) => Color)[] = [
    c => c.lighten(1.18),
    c => c.lighten(0.82),
    c => c.saturate(1.25).lighten(1.1),
    c => c.saturate(0.85).lighten(0.9),
  ];
  let t = 0;
  while (palette.length < NUM_CHART_COLORS) {
    const base = brand[palette.length % brand.length];
    palette.push(transforms[t % transforms.length](base));
    t++;
  }
  return palette.slice(0, NUM_CHART_COLORS).map(c => (isDark ? c.lighten(1.05) : c).toHexString());
}

// ---------------------------------------------------------------------------
// Palette-proximate semantic generation
//
// Brand inputs almost never pin positive/negative colors, so when the spec carries no
// `semantics` we GENERATE close-to-the-palette ones from the 5 brand colors, the way the
// designers authored the built-in palettes' tokens (Ocean's positive #06A588 is a generated
// shade of its #08C8A4 teal, not a palette slot):
//   - a palette green/teal (or a non-primary red/pink/coral) anchors the token's hue and gets
//     re-shaded into a usable semantic lightness band;
//   - with no anchor, a new color is synthesized at the generic semantic hue but carrying the
//     palette's own vibrancy (median OKLCH lightness/chroma), so a pastel brand gets a pastel
//     green/red and a neon brand a vivid one.
// The chart negative and the muted neutral are the same pinned greys as every built-in palette.
// The chart positive may sit on a cool primary (the Dusk purple / Sunrise blue precedent) but
// never on a warm one, where "good" would read as red/orange.
// ---------------------------------------------------------------------------

// Hue anchors for synthesized tokens when the palette has no green/red of its own — the exact
// values the designers picked for Dusk/Coral. Also used verbatim for grayscale brands, where
// there is no vibrancy to harmonize with.
const GENERIC_POSITIVE = "#4EB781";
const GENERIC_NEGATIVE = "#FF3636";

const clamp = (x: number, min: number, max: number) => Math.min(max, Math.max(min, x));

/**
 * Saturated and mid-lightness — usable as a semantic accent (excludes neutrals + extremes).
 * The saturation bar sits at 0.25 so muted brand greens (e.g. a forest green at s≈0.25) still
 * anchor the positive instead of being passed over for a synthesized one.
 */
function isChromatic(c: Color): boolean {
  const { s, l } = c.hsl;
  return s >= 0.25 && l >= 0.2 && l <= 0.78;
}

function isGreenTeal(c: Color): boolean {
  const hue = c.hsl.h * 360;
  return isChromatic(c) && hue >= 100 && hue <= 185;
}

/** True red through pink (h≤20 or h≥330) — deliberately excludes orange (Sunrise's #FF7D1D). */
function isRedFamily(c: Color): boolean {
  const { h, s } = c.hsl;
  const hue = h * 360;
  return isChromatic(c) && s >= 0.55 && (hue <= 20 || hue >= 330);
}

/**
 * Re-shade an in-palette anchor into the semantic lightness band, preserving its hue/chroma —
 * the Ocean #08C8A4 → #06A588 move: close to the palette, distinct from the raw slot.
 */
function toSemanticShade(c: Color): Color {
  const { l, c: chroma, h } = c.oklch;
  return oklch(clamp(l, 0.55, 0.72), chroma, h);
}

/**
 * Generate a brand-new color at the generic token's hue, carrying the palette's own vibrancy
 * (median OKLCH lightness/chroma of its chromatic colors). Grayscale brands have no vibrancy to
 * inherit and keep the generic token as-is.
 */
function synthesizeSemantic(brand: Color[], genericHex: string): Color {
  const generic = Color.parse(genericHex);
  const refs = brand.filter(isChromatic).map(c => c.oklch);
  if (!refs.length) {
    return generic;
  }
  const median = (xs: number[]) => [...xs].sort((a, b) => a - b)[Math.floor(xs.length / 2)];
  const l = clamp(median(refs.map(r => r.l)), 0.55, 0.75);
  const chroma = clamp(median(refs.map(r => r.c)), 0.08, 0.17);
  return oklch(l, chroma, generic.oklch.h);
}

function deriveSemanticsFromBrand(brand: Color[]): Required<NonNullable<ThemeSpec["semantics"]>> {
  const primary = brand[0];
  // Prefer a non-primary green so the positive doesn't collide with the dominant series color
  // (Garden's chart positive is its second green, not its green primary).
  const green = [...brand.slice(1), primary].find(isGreenTeal);
  // The negative must never be the primary: on an all-red brand (Coral) the primary is the main
  // accent, and coloring "bad" with it would make every negative delta look on-brand-highlighted.
  const red = brand.slice(1).find(isRedFamily);
  const primaryHueDeg = primary.hsl.h * 360;
  const primaryIsCool = isChromatic(primary) && primaryHueDeg > 90 && primaryHueDeg < 330;

  const positive = green ? toSemanticShade(green) : synthesizeSemantic(brand, GENERIC_POSITIVE);
  const negative = red ? toSemanticShade(red) : synthesizeSemantic(brand, GENERIC_NEGATIVE);
  return {
    positive: positive.toHexString(),
    negative: negative.toHexString(),
    neutral: PALETTE_NEUTRAL,
    chart_positive: (green || !primaryIsCool ? positive : primary).toHexString(),
    chart_negative: PALETTE_CHART_NEGATIVE,
  };
}

/**
 * Resolve the semantic tokens. TEXT tokens (KPI delta text, `.metric-status-*`) start from the
 * spec's pinned values (built-in palettes carry the designer-authored Figma colors) or the
 * palette-proximate derivation above, then get nudged for legibility against the card surface —
 * `ensureContrast` preserves hue/chroma, so "good=green" survives a magenta or grayscale brand.
 * CHART tokens are used verbatim (the design fills whole bars/areas with them — e.g. the
 * universal 40%-grey chart negative — so text-contrast nudging would distort them).
 */
function buildSemanticTokens(
  cardBg: Color,
  brand: Color[],
  override?: ThemeSpec["semantics"]
): {
  text: { positive: string; negative: string; neutral: string };
  chart: { positive: string; negative: string; neutral: string };
} {
  const derived = deriveSemanticsFromBrand(brand);
  const legible = (hex: string) => ensureContrast(Color.parse(hex), cardBg, 4.5).toHexString();
  const text = {
    positive: legible(override?.positive ?? derived.positive),
    negative: legible(override?.negative ?? derived.negative),
    neutral: legible(override?.neutral ?? derived.neutral),
  };
  // Explicitly pinned text tokens cascade to the charts; the derivation fills in only when the
  // spec pins nothing at all for that token.
  const chart = {
    positive: override?.chart_positive ?? override?.positive ?? derived.chart_positive,
    negative: override?.chart_negative ?? override?.negative ?? derived.chart_negative,
    neutral: text.neutral,
  };
  return { text, chart };
}

/** Generate a few Tailwind arbitrary-value gradient class pairs from the brand colors. */
function buildCardGradients(brand: Color[]): string[] {
  const hexes = brand.map(c => c.toHexString());
  const pairs: string[] = [];
  for (let i = 0; i < hexes.length; i++) {
    const from = hexes[i];
    const to = hexes[(i + 1) % hexes.length];
    pairs.push(`from-[${from}] to-[${to}]`);
  }
  return pairs;
}

// ---------------------------------------------------------------------------
// Page background
//
// The Figma design ("Dashboard Themes v1") splits the page background into a 700px-tall "header"
// band of soft colour blobs and a faint "page" glow that fills the rest of the body, both painted
// over a flat base colour. Built-in palettes carry the designer-authored layer data VERBATIM in
// `PALETTE_BACKGROUNDS` (each palette's frames are hand-tuned — colours, opacities and even
// matrices differ between palettes, so they cannot be derived from the 5 brand colours). Custom
// themes derive an Ocean-shaped template from their brand colours instead.
// ---------------------------------------------------------------------------

const GRAD_FRAME_W = 1781; // Figma frame width (matrix coordinate space)
const HEADER_H = 700; // header band height (viewBox height AND on-screen height)

/** Figma radial vector layer as an SVG rect + gradient def (verbatim gradientTransform). */
function radialSvgParts(l: RadialLayer, id: string): { def: string; rect: string } {
  const stops = l.stops
    .map(([color, offset]) => `<stop stop-color='${color}' offset='${offset}'/>`)
    .join("");
  return {
    def:
      `<radialGradient id='${id}' gradientUnits='userSpaceOnUse' cx='0' cy='0' r='10' ` +
      `gradientTransform='matrix(${l.matrix.join(" ")})'>${stops}</radialGradient>`,
    rect: `<rect x='0' y='0' height='100%' width='100%' fill='url(#${id})' opacity='${l.opacity}'/>`,
  };
}

/**
 * CSS linear-gradient angle → SVG userSpaceOnUse endpoints for the same viewBox, so a linear layer
 * can live inside a combined SVG. CSS angles are clockwise from "to top"; the gradient line is
 * centred with length |W·sinθ| + |H·cosθ| (the CSS spec's formula).
 */
function linearSvgParts(
  l: LinearLayer,
  w: number,
  h: number,
  id: string
): { def: string; rect: string } {
  const rad = (l.angle * Math.PI) / 180;
  const dx = Math.sin(rad);
  const dy = -Math.cos(rad);
  const len = Math.abs(w * dx) + Math.abs(h * dy);
  const x1 = w / 2 - (dx * len) / 2;
  const y1 = h / 2 - (dy * len) / 2;
  const x2 = w / 2 + (dx * len) / 2;
  const y2 = h / 2 + (dy * len) / 2;
  const stops = l.stops
    .map(([color, pct]) => `<stop stop-color='${color}' offset='${pct / 100}'/>`)
    .join("");
  const round = (n: number) => Number(n.toFixed(2));
  return {
    def: `<linearGradient id='${id}' gradientUnits='userSpaceOnUse' x1='${round(x1)}' y1='${round(
      y1
    )}' x2='${round(x2)}' y2='${round(y2)}'>${stops}</linearGradient>`,
    rect: `<rect x='0' y='0' height='100%' width='100%' fill='url(#${id})'/>`,
  };
}

const svgDataUri = (svg: string): string =>
  `url("data:image/svg+xml;utf8,${svg.replace(/#/g, "%23")}")`;

// The header overlays fade to 0 alpha over the band's last 100px. In Figma the 700px frame just
// clips them — a few layers still carry ~3-6% alpha at the edge, which reads as a faint horizontal
// line against the flat body (invisible in the mocks, visible on a real dashboard). The fade only
// touches the overlay glows; the base wash underneath is already flat (= body color) by the edge.
const HEADER_FADE_START = 600;

/**
 * All header overlay layers combined into ONE SVG (painted like stacked CSS layers) with an alpha
 * mask that fades out the bottom of the band so nothing gets hard-clipped at the boundary.
 */
function headerOverlaySvg(layers: GradientLayer[]): string {
  const parts = layers.map((l, i) =>
    l.kind === "radial"
      ? radialSvgParts(l, `g${i}`)
      : linearSvgParts(l, GRAD_FRAME_W, HEADER_H, `g${i}`)
  );
  // SVG paints in document order (later = on top); the stored layers are top-first, so reverse.
  const rects = parts
    .map(p => p.rect)
    .reverse()
    .join("");
  const defs = parts.map(p => p.def).join("");
  const fade =
    `<linearGradient id='fade' gradientUnits='userSpaceOnUse' x1='0' y1='${HEADER_FADE_START}' ` +
    `x2='0' y2='${HEADER_H}'><stop stop-color='white' offset='0'/>` +
    `<stop stop-color='white' stop-opacity='0' offset='1'/></linearGradient>` +
    `<mask id='fadeMask'><rect x='0' y='0' width='100%' height='100%' fill='url(#fade)'/></mask>`;
  return svgDataUri(
    `<svg viewBox='0 0 ${GRAD_FRAME_W} ${HEADER_H}' xmlns='http://www.w3.org/2000/svg' ` +
      `preserveAspectRatio='none'><g mask='url(#fadeMask)'>${rects}</g><defs>${defs}${fade}</defs></svg>`
  );
}

/** One gradient layer as a standalone CSS background image (Figma's own export encoding). */
function layerCss(l: GradientLayer): string {
  if (l.kind === "linear") {
    const stops = l.stops.map(([color, pct]) => `${color} ${pct}%`).join(", ");
    return `linear-gradient(${l.angle}deg, ${stops})`;
  }
  const { def, rect } = radialSvgParts(l, "grad");
  return svgDataUri(
    `<svg viewBox='0 0 ${GRAD_FRAME_W} ${HEADER_H}' xmlns='http://www.w3.org/2000/svg' ` +
      `preserveAspectRatio='none'>${rect}<defs>${def}</defs></svg>`
  );
}

/**
 * The page-glow section as ONE combined SVG so the Figma GROUP opacity composites the layers
 * together first (element opacity ≠ per-layer opacity when layers overlap).
 */
function pageSectionSvg(page: ModeBackground["page"]): string {
  const [w, h] = page.viewBox;
  const parts = page.layers.map((l, i) =>
    l.kind === "radial" ? radialSvgParts(l, `g${i}`) : linearSvgParts(l, w, h, `g${i}`)
  );
  // SVG paints in document order (later = on top); the stored layers are top-first, so reverse.
  const rects = parts
    .map(p => p.rect)
    .reverse()
    .join("");
  const defs = parts.map(p => p.def).join("");
  // Short fade-in at the section top: some Figma page rects were authored overlapping the header
  // band (e.g. Berry dark starts at y=648), so their top edge carries a few % alpha when anchored
  // at the 700px boundary — the fade removes the resulting hairline without visible effect.
  const fade =
    `<linearGradient id='fade' gradientUnits='userSpaceOnUse' x1='0' y1='0' x2='0' y2='60'>` +
    `<stop stop-color='white' stop-opacity='0' offset='0'/>` +
    `<stop stop-color='white' offset='1'/></linearGradient>` +
    `<mask id='fadeMask'><rect x='0' y='0' width='100%' height='100%' fill='url(#fade)'/></mask>`;
  return svgDataUri(
    `<svg viewBox='0 0 ${w} ${h}' xmlns='http://www.w3.org/2000/svg' preserveAspectRatio='none'>` +
      `<g opacity='${page.opacity}' mask='url(#fadeMask)'>${rects}</g><defs>${defs}${fade}</defs></svg>`
  );
}

/**
 * Assemble a mode's layer spec into a multi-layer CSS `background` value (first layer = top):
 * header layers + base wash pinned to the 700px top band, the page glow stretched over the rest of
 * the page, and the flat base colour (the wash's end stop) backing everything.
 */
function renderModeBackground(mode: ModeBackground): string {
  const headerLayer = (image: string) => `${image} 0 0 / 100% ${HEADER_H}px no-repeat`;
  const layers = [
    headerLayer(headerOverlaySvg(mode.header)),
    headerLayer(layerCss(mode.headerBase)),
  ];
  layers.push(
    `${pageSectionSvg(
      mode.page
    )} 0 ${HEADER_H}px / 100% max(0px, calc(100% - ${HEADER_H}px)) no-repeat`
  );
  const base = mode.headerBase.stops[mode.headerBase.stops.length - 1][0];
  layers.push(`linear-gradient(${base}, ${base})`);
  return layers.join(", ");
}

/** Fixed page colours for the background-OFF toggle (solid in light, subtle gradient in dark). */
function renderNoBackground(noBg: PaletteBackground["noBg"], isDark: boolean): string {
  return isDark ? `linear-gradient(180deg, ${noBg.dark[0]}, ${noBg.dark[1]})` : noBg.light;
}

/** Parse an optional #hex from the spec defensively (the value round-trips LLM extraction). */
function parseOptionalHex(hex?: string): Color | undefined {
  const trimmed = hex?.trim();
  return trimmed && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(trimmed)
    ? Color.parse(trimmed)
    : undefined;
}

/**
 * Ocean-template background for custom themes (no designer-authored frame): the canonical Figma
 * layer geometry (2884:48806) recoloured from the brand slots — blue = primary (c0), cyan = c1,
 * teal = c3, sheen = c4 — with base washes derived from the primary/secondary hues. Explicitly
 * `pinned` page colors (the brand's own light/dark site backgrounds, from extraction) replace the
 * derived base washes and the background-OFF page colors; the overlay glows stay brand-derived.
 */
function deriveBackgroundFromBrand(
  brand: Color[],
  pinned?: { light?: Color; dark?: Color }
): PaletteBackground {
  const at = (i: number) => brand[i] ?? brand[brand.length - 1];
  const c0 = at(0);
  const c1 = at(1);
  const c3 = at(3);
  const c4 = at(4);
  const rgba = (c: Color, alpha: number) => c.withAlpha(alpha).toRgbString();
  const hex = (c: Color) => c.toHexString();

  const radial = (
    opacity: number,
    matrix: RadialLayer["matrix"],
    color: Color,
    startOffset = 0
  ): RadialLayer => ({
    kind: "radial",
    opacity,
    matrix,
    stops: [
      [hex(color), startOffset],
      [rgba(color, 0), 1],
    ],
  });

  // Light base wash: pale tint of the secondary hue easing into a near-white tint of the primary.
  const lightTop = Color.fromHSL({ h: c1.hsl.h, s: Math.min(0.85, c1.hsl.s), l: 0.86 });
  const lightBottom = pinned?.light ?? Color.fromHSL({ h: c0.hsl.h, s: 0.5, l: 0.96 });
  // Dark base wash: near-black navy from the primary hue with a translucent c1 mid-glow.
  const navy = pinned?.dark ?? Color.fromHSL({ h: c0.hsl.h, s: 0.45, l: 0.11 });
  // Page radial mid/end: violet-ish shifts of the primary (Figma light: blue→violet→purple).
  const pageMid = c0.saturate(1.15).lighten(0.72);
  const pageEnd = c0.saturate(1.2).lighten(0.5);

  const pageBand = (angle: number): LinearLayer => ({
    kind: "linear",
    angle,
    stops: [
      [rgba(c1, 0), 23.111],
      [hex(c1), 36.318],
      [hex(c3), 42.909],
      [rgba(c3, 0), 53.897],
    ],
  });

  return {
    light: {
      header: [
        {
          kind: "linear",
          angle: 12.09477,
          stops: [
            ["rgba(255,255,255,0)", 38.318],
            ["rgba(255,255,255,0.5)", 54.504],
            ["rgba(255,255,255,0)", 70.505],
          ],
        },
        radial(0.3, [-28.8, 0, 0, -27.475, 1550, 350], c0),
        radial(0.9, [48.6, 0, 0, 33.572, 58, 61.25], WHITE),
        radial(0.35, [32.111, 7.525, -2.8255, 36.925, 466, 330.75], c0),
        radial(0.3, [30.5, 39.55, -22.6, 53.375, 37, 47.25], c3, 0.46635),
        radial(0.3, [84.1, 1.75, -0.52073, 25.025, 940, 428.75], c3),
      ],
      headerBase: {
        kind: "linear",
        angle: 180,
        stops: [
          [hex(lightTop), 3.0848],
          [hex(lightBottom), 80.72],
        ],
      },
      page: {
        opacity: 0.1,
        viewBox: [GRAD_FRAME_W, 1296],
        layers: [
          pageBand(336.77281),
          {
            kind: "radial",
            opacity: 1,
            matrix: [-89.05, -64.8, 89.05, -64.8, 890.5, 648],
            stops: [
              [hex(c0), 0],
              [rgba(pageMid, 0.5), 0.3],
              [rgba(pageEnd, 0), 0.6],
            ],
          },
        ],
      },
    },
    dark: {
      header: [
        {
          kind: "linear",
          angle: 16.26021,
          stops: [
            [rgba(c4, 0), 39.416],
            [rgba(c4, 0.2), 50.7],
            [rgba(c4, 0), 62.425],
          ],
        },
        radial(0.1, [-28.8, 0, 0, -27.475, 1550, 350], c4),
        radial(0.9, [48.6, 0, 0, 33.572, 58, 61.25], Color.parse("#0A0B0F")),
        radial(0.4, [32.111, 7.525, -2.8255, 36.925, 466, 330.75], c0),
        radial(0.3, [30.5, 39.55, -22.6, 53.375, 37, 47.25], c3, 0.46635),
        radial(0.3, [84.1, 1.75, -0.20833, 30.661, 940, 428.75], c3),
      ],
      headerBase: {
        kind: "linear",
        angle: 180,
        stops: [
          [hex(navy), 9.4053],
          [rgba(c1, 0.6), 40.026],
          [hex(navy), 80.72],
        ],
      },
      page: {
        opacity: 0.2,
        viewBox: [GRAD_FRAME_W, 1293],
        layers: [
          pageBand(336.82089),
          {
            kind: "radial",
            opacity: 1,
            matrix: [-89.05, -64.65, 89.05, -64.65, 890.5, 646.5],
            stops: [
              [hex(c0), 0],
              [rgba(c0, 0), 0.6],
            ],
          },
        ],
      },
    },
    noBg: {
      light: (pinned?.light ?? Color.fromHSL({ h: c0.hsl.h, s: 0.85, l: 0.96 })).toHexString(),
      dark: pinned?.dark
        ? [pinned.dark.toHexString(), pinned.dark.lighten(1.4).toHexString()]
        : ["#0A0B0F", Color.fromHSL({ h: c0.hsl.h, s: 0.55, l: 0.12 }).toHexString()],
    },
  };
}

const FONT_FAMILY = "Google Sans Flex, Inter, system-ui, -apple-system, sans-serif";

export function buildThemeFromSpec(spec: ThemeSpec, themeId?: string): Theme {
  const isDark = spec.mode === "dark";
  const brand = (spec.colors.length ? spec.colors : ["#153AF0"]).map(c => Color.parse(c));
  const primary = brand[0];
  const secondary = brand[1] ?? primary;
  const { h: hue, c: primaryChroma } = primary.oklch;
  // Subtle brand tint in surfaces; keep chroma low so text stays readable and it doesn't overwhelm.
  const surfaceChroma = Math.min(0.02, primaryChroma * 0.3);

  // ------------------------------------------------------------------
  // Surfaces (page / main / nav / card)
  // ------------------------------------------------------------------
  const mainBg = isDark ? oklch(0.17, surfaceChroma, hue) : oklch(0.985, surfaceChroma, hue);
  const cardBg = isDark ? DARK_CARD_SURFACE : WHITE;
  const navBg = isDark ? oklch(0.2, surfaceChroma, hue) : oklch(0.995, surfaceChroma, hue);
  const foreground = isDark ? oklch(0.93, 0.006, hue) : NEAR_BLACK;
  const rawMutedForeground = isDark ? oklch(0.72, 0.01, hue) : oklch(0.5, 0.01, hue);
  const borderColor = isDark ? oklch(0.32, surfaceChroma, hue) : oklch(0.9, surfaceChroma, hue);
  // Guarantee the muted/secondary text is legible on the main surface.
  const mutedForeground = ensureContrast(rawMutedForeground, mainBg, 4.5);

  const hoverBgOnSurface = isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.05)";

  const buildSurface = (bg: Color, cardHover?: string) => ({
    backgroundColor: bg.toHexString(),
    foregroundColor: foreground.toHexString(),
    mutedForegroundColor: mutedForeground.toHexString(),
    borderColor: borderColor.toHexString(),
    borderWidth: "1px",
    shadow: isDark
      ? "0 1px 3px 0 rgb(0 0 0 / 0.3), 0 1px 2px -1px rgb(0 0 0 / 0.3)"
      : "0 1px 4px 0 rgb(0 0 0 / 0.05), 0 1px 2px -1px rgb(0 0 0 / 0.05)",
    textButton: {
      hoverBackgroundColor: cardHover ?? hoverBgOnSurface,
      hoverForegroundColor: foreground.toHexString(),
    },
  });

  // ------------------------------------------------------------------
  // Buttons
  // ------------------------------------------------------------------
  const buildButton = (base: Color) => {
    const fg = readableText(base);
    // Dark buttons brighten on hover, light buttons deepen — keeps the hover state visible.
    const hover = base.isDark() ? base.lighten(1.15) : base.lighten(0.88);
    return {
      backgroundColor: base.toHexString(),
      foregroundColor: fg.toHexString(),
      hoverBackgroundColor: hover.toHexString(),
      hoverForegroundColor: fg.toHexString(),
      shadow: "none",
      borderColor: "transparent",
    };
  };

  // ------------------------------------------------------------------
  // Page background
  // ------------------------------------------------------------------
  // Built-in palettes use their designer-authored Figma layers; custom themes derive the Ocean
  // template. Background OFF still pins the fixed per-theme page colors from the design.
  const backgroundSpec =
    (themeId && PALETTE_BACKGROUNDS[themeId]) ||
    deriveBackgroundFromBrand(brand, {
      light: parseOptionalHex(spec.background.light_color),
      dark: parseOptionalHex(spec.background.dark_color),
    });
  const pageBackground =
    spec.background.type === "gradient"
      ? renderModeBackground(backgroundSpec[isDark ? "dark" : "light"])
      : renderNoBackground(backgroundSpec.noBg, isDark);

  // ------------------------------------------------------------------
  // Chart.js palette + styling
  // ------------------------------------------------------------------
  const chartPalette = buildChartPalette(brand, isDark);
  const semanticTokens = buildSemanticTokens(cardBg, brand, spec.semantics);
  // Figma card surface: translucent at the light corner so the page background bleeds through,
  // solid by mid-card. Shared by dashboard cards and text blocks.
  const cardSurfaceBackground = `linear-gradient(247.73deg, ${cardBg
    .withAlpha(0.6)
    .toRgbString()} 4.75%, ${cardBg.toHexString()} 56.31%)`;
  const cardShadow = "0px 12px 24px rgba(0, 0, 0, 0.06)";
  const gridColor = borderColor.toHexString();
  const gridBorderColor = (
    isDark ? borderColor.lighten(1.2) : borderColor.lighten(0.9)
  ).toHexString();

  const theme: Theme = {
    themeId: themeId ?? `custom:${spec.name || "theme"}`,
    themeName: spec.name || "Custom Theme",
    logoVariant: isDark ? "light_text" : "dark",
    semanticText: semanticTokens.text,
    tailwind: {
      general: {
        fontFamily: FONT_FAMILY,
        // Explicit px: the iframe root pins font-size to 14px, so rem radii shrink to 87.5%.
        borderRadius: "12px",
        pageBackground,
        cardBackgroundGradients: buildCardGradients(brand),
        isDarkTheme: isDark,
      },
      main: buildSurface(mainBg),
      nav: buildSurface(navBg),
      card: {
        ...buildSurface(
          cardBg,
          isDark ? "rgba(255,255,255,0.08)" : oklch(0.95, surfaceChroma, hue).toHexString()
        ),
        cardSurfaceBackground,
        shadow: cardShadow,
      },
      // Text blocks are plain light/dark cards (no brand gradient): white surface + dark text in
      // light mode, dark surface + light text in dark mode.
      textBlock: {
        background: cardSurfaceBackground,
        foregroundColor: foreground.toHexString(),
        border: `1px solid ${borderColor.toHexString()}`,
      },
      primaryButton: buildButton(primary),
      secondaryButton: buildButton(secondary),
    },
    chartjs: {
      defaultColor: foreground.toHexString(),
      defaultBackgroundColor: primary.withAlpha(0.8).toRgbString(),
      defaultBorderColor: "transparent",
      colorPalette: chartPalette as ThemeChartPalette,
      positive: semanticTokens.chart.positive,
      negative: semanticTokens.chart.negative,
      neutral: semanticTokens.chart.neutral,
      gridColor,
      gridBorderColor,
      fontFamily: FONT_FAMILY,
      fontSize: 12,
      tooltipBackgroundColor: (isDark ? cardBg.lighten(1.2) : NEAR_BLACK).toHexString(),
      tooltipTextColor: isDark ? foreground.toHexString() : "#FFFFFF",
      tooltipBorderColor: gridBorderColor,
      legendTextColor: foreground.toHexString(),
      axisTickColor: mutedForeground.toHexString(),
      lineTension: 0.3,
      lineBorderWidth: 2,
      pointRadius: 0,
      pointHoverRadius: 5,
      pointBorderWidth: 2,
      barBorderWidth: 0,
      barBorderRadius: 4,
      // Solid bars per design feedback: full-opacity fills, no barGradient plugin.
      barBackgroundOpacity: 1,
      plugins: [
        createLineFillGradientPlugin(
          isDark
            ? {
                bottomOpacity: 0.1,
                topOpacity: 0.6,
                topLuminosity: 0.9,
                bottomLuminosity: 1.0,
                gradientEndPosition: 0.5,
              }
            : {
                bottomOpacity: 0.05,
                topOpacity: 0.5,
                topLuminosity: 1.0,
                bottomLuminosity: 1.1,
                gradientEndPosition: 0.5,
              }
        ),
      ],
    },
    agGrid: {
      baseTheme: "material",
      params: {
        browserColorScheme: isDark ? "dark" : "light",
        fontFamily: FONT_FAMILY,
        accentColor: primary.toHexString(),
        ...(isDark
          ? {
              backgroundColor: cardBg.toHexString(),
              foregroundColor: foreground.toHexString(),
              borderColor: borderColor.toHexString(),
              headerBackgroundColor: mainBg.toHexString(),
              headerTextColor: foreground.toHexString(),
              oddRowBackgroundColor: mainBg.toHexString(),
            }
          : {}),
        fontSize: 12,
        headerFontSize: 12,
        headerFontWeight: 800,
        spacing: 4,
        borderRadius: 4,
        wrapperBorderRadius: 4,
        cellHorizontalPaddingScale: 0.9,
        headerVerticalPaddingScale: 1.2,
        rowVerticalPaddingScale: 0.9,
      },
    },
  };

  return theme;
}

// The chart palette field is a 12-tuple; we build a plain string[] and assert the length.
type ThemeChartPalette = Theme["chartjs"]["colorPalette"];
